import AppComponent from '../base/app-component.ts';
import type { DataTable, ColumnDef } from './data-table.ts';
import { ModaleColore } from '../lib/modale-colore.ts';
import { ModaleModificaArchivio } from '../lib/modale-modifica-archivio.ts';
import type { RecordDaModificare } from '../lib/modale-modifica-archivio.ts';
import { raccogliDateFilters, handleDateOpChange, resetDateRanges, contaFiltri } from '../lib/date-filter.ts';
import { buildExportQuery, scaricaFile } from '../lib/utils.ts';
import { enforcePerm } from '../lib/perm.ts';

const COLUMNS: ColumnDef[] = [
  { key: 'codFiscale', label: 'Codice Fiscale', type: 'code' },
  { key: 'cognome', label: 'Cognome/Nome', type: 'text' },
  { key: 'dataNascita', label: 'Data nascita', type: 'date', emptyText: '-' },
  { key: 'statoCe', label: 'CE', type: 'text' },
  { key: 'statoEc', label: 'EC', type: 'text' },
  { key: 'operatore', label: 'Operatore', type: 'text' },
  { key: 'modalitaRaccolta', label: 'Raccolta', type: 'text' },
  { key: 'datapres', label: 'Data raccolta', type: 'date' },
  { key: 'createdAt', label: 'Inserito il', type: 'date', emptyText: '-' },
  { key: 'origine', label: 'Origine', type: 'text' },
];

/** Campi filtro letti dalle select dell'offcanvas */
const CAMPI_SELECT = ['colore', 'modalitaRaccolta'];

const nominativo = (row: Record<string, unknown>): string =>
  [row.cognome, row.nome].filter(Boolean).join(' ') || String(row.codFiscale);

export class Archivio extends AppComponent {
  static override componentName = 'wc-archivio';
  protected override get templateId() { return 'archivio'; }

  private dataTable: DataTable | null = null;
  private currentPage = 1;
  private currentFilter = '';
  private filtroOrigine = '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private offcanvas: any = null;
  private modaleColore: ModaleColore | null = null;
  private modaleModifica: ModaleModificaArchivio | null = null;

  protected override buildRenderingData(): Record<string, unknown> {
    return {
      ...super.buildRenderingData(),
      columnsJson: JSON.stringify(COLUMNS),
    };
  }

  protected override onDisconnected(): void {
    this.modaleColore?.dispose();
    this.modaleModifica?.dispose();
  }

  protected override afterRender(): void {
    this.dataTable = this.$('wc-data-table') as DataTable | null;
    if (!this.dataTable) return;
    const modaleEl = this.$('[data-modal="cambia-colore"]');
    if (modaleEl) this.modaleColore = new ModaleColore(modaleEl);
    const modaleModificaEl = this.$('[data-modal="modifica-archivio"]');
    if (modaleModificaEl) this.modaleModifica = new ModaleModificaArchivio(modaleModificaEl);
    this.setupEvents();
    this.setupFiltriAvanzati();
    this.setupExportExcel();
    this.carica();
  }

  private setupEvents(): void {
    if (!this.dataTable) return;

    this.listen(this.dataTable, 'dt-page-change', (e: Event) => {
      this.currentPage = (e as CustomEvent).detail.page;
      this.carica();
    });

    this.listen(this.dataTable, 'dt-filter-change', (e: Event) => {
      this.currentFilter = (e as CustomEvent).detail.filter;
      this.currentPage = 1;
      this.carica();
    });

    this.listen(this.dataTable, 'dt-render-complete', () => enforcePerm(this.dataTable!));

    this.listen(this.dataTable, 'dt-row-action', (e: Event) => {
      const { action, row } = (e as CustomEvent).detail;
      if (action === 'elimina') this.elimina(row);
      if (action === 'colore') this.apriCambioColore(row);
      if (action === 'modifica') this.apriModifica(row);
    });

    // Filtri rapidi origine
    this.delegate.on('click', '[data-filtro-rapido="origine"] button', (e: Event) => {
      const btn = (e.target as HTMLElement).closest('button') as HTMLElement;
      const group = btn.closest('[data-filtro-rapido]') as HTMLElement;
      const value = btn.dataset.value!;
      const wasActive = btn.classList.contains('active');

      group.querySelectorAll('button').forEach(b => b.classList.remove('active'));
      if (!wasActive) btn.classList.add('active');

      this.filtroOrigine = wasActive ? '' : value;
      this.currentPage = 1;
      this.carica();
    });
  }

  private async elimina(row: Record<string, unknown>): Promise<void> {
    const ok = await Weext.confirm(
      `Eliminare il record archivio per ${row.codFiscale}?`,
      { title: 'Conferma eliminazione', confirmText: 'Elimina', confirmClass: 'btn-danger' }
    );
    if (!ok) return;
    const result = await this.api.archivio.elimina(Number(row.id));
    if (!result.ok) {
      Weext.toast(result.error, { type: 'danger' });
      return;
    }
    Weext.toast('Record eliminato', { type: 'success' });
    this.carica();
  }

  private apriModifica(row: Record<string, unknown>): void {
    const record = { ...row, nominativo: nominativo(row) } as unknown as RecordDaModificare;
    this.modaleModifica?.apri(record, async (dati) => {
      const result = await this.api.archivio.aggiorna(Number(row.id), dati);
      if (!result.ok) {
        Weext.toast(result.error, { type: 'danger' });
        return false;
      }
      Weext.toast('Record aggiornato', { type: 'success' });
      this.carica();
      return true;
    });
  }

  private apriCambioColore(row: Record<string, unknown>): void {
    this.modaleColore?.apri(nominativo(row), row.coloreId as number | null, async (coloreId) => {
      const result = await this.api.archivio.cambiaColore(Number(row.id), coloreId);
      if (!result.ok) {
        Weext.toast(result.error, { type: 'danger' });
        return false;
      }
      Weext.toast('Colore aggiornato', { type: 'success' });
      this.carica();
      return true;
    });
  }

  private setupFiltriAvanzati(): void {
    const offcanvasEl = this.$('[data-offcanvas="filtri"]');
    if (offcanvasEl) {
      this.offcanvas = new (globalThis as any).bootstrap.Offcanvas(offcanvasEl);
    }

    this.delegate.on('click', '[data-action="apri-filtri"]', () => this.offcanvas?.show());

    const form = this.$('[data-form="filtri-avanzati"]') as HTMLFormElement | null;
    if (form) {
      this.listen(form, 'submit', (e: Event) => {
        e.preventDefault();
        this.offcanvas?.hide();
        this.currentPage = 1;
        this.carica();
      });
    }

    this.delegate.on('click', '[data-action="reset-filtri"], [data-action="reset-filtri-toolbar"]', () => {
      form?.reset();
      resetDateRanges(this);
      this.offcanvas?.hide();
      this.currentPage = 1;
      this.carica();
    });

    this.delegate.on('change', '[data-date-op]', (e: Event) => handleDateOpChange(this, e));
  }

  /** Filtri dell'offcanvas (select + data nascita + data inserimento) */
  private raccogliFiltri(): Record<string, string> {
    const params: Record<string, string> = {};
    for (const campo of CAMPI_SELECT) {
      const val = (this.$(`[data-filtro-val="${campo}"]`) as HTMLSelectElement | null)?.value;
      if (val) params[campo] = val;
    }
    return { ...params, ...raccogliDateFilters(this, ['dataNascita', 'dataInserimento']) };
  }

  /** Excel con gli stessi filtri della lista, origine rapida inclusa */
  private setupExportExcel(): void {
    this.delegate.on('click', '[data-action="export-excel"]', (e: Event) => {
      e.preventDefault();
      const qs = buildExportQuery({ ...this.raccogliFiltri(), origine: this.filtroOrigine }, this.currentFilter);
      scaricaFile(`/api/archivio/export-csv?${qs}`);
    });
  }

  private updateFiltriCount(filtri: Record<string, string>): void {
    const count = contaFiltri(filtri);
    const badge = this.$('[data-filtri-count]');
    if (badge) {
      badge.textContent = count > 0 ? String(count) : '';
      badge.classList.toggle('d-none', count === 0);
    }
    this.$('[data-action="reset-filtri-toolbar"]')?.classList.toggle('d-none', count === 0);
  }

  private async carica(): Promise<void> {
    if (!this.dataTable) return;
    this.dataTable.setLoading();

    const filtri = this.raccogliFiltri();
    this.updateFiltriCount(filtri);

    const params: Record<string, string | number | undefined> = {
      page: this.currentPage,
      limit: 30,
      q: this.currentFilter || undefined,
      origine: this.filtroOrigine || undefined,
      ...filtri,
    };

    const result = await this.api.archivio.lista(params);
    if (!result.ok) {
      this.dataTable.setError(result.error);
      return;
    }

    const { archivio, count, totalPages, pageSize, page } = result.data;
    this.dataTable.setData(archivio as unknown as Record<string, unknown>[], {
      page, totalPages, count, pageSize,
    });
  }
}
